import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

interface DefinitionProps {
  term: string;
  text: string;
}

/** The tooltip keeps this far from the edge of the window. */
const EDGE = 8;
/** The pointer may leave the button and cross the tile on its way to the tooltip without closing it. */
const CLOSE_DELAY_MS = 250;

/**
 * An info button that shows what a term means (item 5). It opens on hover and on keyboard focus, and a
 * click pins it open for touch screens. Escape closes it, and the pointer can move onto it without it
 * disappearing, along any path (a short delay on leaving; WCAG 1.4.13). The description is always in the page, tied to the button with
 * `aria-describedby`, and only hidden from the eye until the tooltip opens. It slides sideways to stay
 * inside the window.
 */
const Definition: React.FC<DefinitionProps> = ({ term, text }) => {
  const id = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [shift, setShift] = useState(0);
  const rootRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const leaveTimer = useRef<number | undefined>(undefined);
  const open = !dismissed && (hovered || focused || pinned);

  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    const onPress = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setPinned(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPress);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPress);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const rect = tipRef.current?.getBoundingClientRect();
    if (!rect) return;
    // clientWidth leaves out the scrollbar; it is 0 where nothing is laid out (tests).
    const width = document.documentElement.clientWidth || window.innerWidth;
    let dx = 0;
    if (rect.right > width - EDGE) dx = width - EDGE - rect.right;
    if (rect.left + dx < EDGE) dx = EDGE - rect.left;
    setShift(dx);
  }, [open]);

  return (
    <span
      ref={rootRef}
      className="ui-def"
      onMouseEnter={() => {
        window.clearTimeout(leaveTimer.current);
        setDismissed(false);
        setHovered(true);
      }}
      onMouseLeave={() => {
        window.clearTimeout(leaveTimer.current);
        leaveTimer.current = window.setTimeout(() => setHovered(false), CLOSE_DELAY_MS);
      }}
    >
      <button
        type="button"
        className="ui-def-btn"
        aria-label={`What is ${term}?`}
        aria-describedby={id}
        onFocus={() => {
          setDismissed(false);
          setFocused(true);
        }}
        onBlur={() => setFocused(false)}
        onClick={() => {
          setDismissed(false);
          setPinned((value) => !value);
        }}
      >
        <Info size={14} strokeWidth={1.5} aria-hidden />
      </button>
      <span
        ref={tipRef}
        role="tooltip"
        id={id}
        className={open ? 'ui-def-tip' : 'sr-only'}
        style={open && shift ? { transform: `translateX(${shift}px)` } : undefined}
      >
        <b>{term}</b>{' '}
        {text}
      </span>
    </span>
  );
};

export default Definition;
